/**
 * EntityMention Extension for Tiptap
 * Renders inline entity mentions with colored styling
 */

import { Mark, mergeAttributes } from '@tiptap/core'

// ============================================================================
// CONFIRMED ENTITY MENTION (styled badge)
// ============================================================================

export interface EntityMentionOptions {
  HTMLAttributes: Record<string, unknown>
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    entityMention: {
      setEntityMention: (attributes: {
        entityType: string
        entityId?: string
      }) => ReturnType
      unsetEntityMention: () => ReturnType
    }
  }
}

export const EntityMention = Mark.create<EntityMentionOptions>({
  name: 'entityMention',

  addOptions() {
    return {
      HTMLAttributes: {},
    }
  },

  addAttributes() {
    return {
      entityType: {
        default: 'Problem',
        parseHTML: (element) => element.getAttribute('data-entity-type'),
        renderHTML: (attributes) => ({
          'data-entity-type': attributes.entityType,
        }),
      },
      entityId: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-entity-id'),
        renderHTML: (attributes) => {
          if (!attributes.entityId) return {}
          return { 'data-entity-id': attributes.entityId }
        },
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-entity-type]',
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    const entityType = HTMLAttributes['data-entity-type'] || 'Problem'

    return [
      'span',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        class: `entity-mention entity-mention--${entityType.toLowerCase()}`,
      }),
      0,
    ]
  },

  addCommands() {
    return {
      setEntityMention:
        (attributes) =>
        ({ commands }) => {
          return commands.setMark(this.name, attributes)
        },
      unsetEntityMention:
        () =>
        ({ commands }) => {
          return commands.unsetMark(this.name)
        },
    }
  },
})

// Entity type icons for display
export const ENTITY_ICONS: Record<string, string> = {
  Problem: '!',
  Person: '@',
  Decision: '↗',
  Risk: '⚠',
  Opportunity: '★',
  ActionItem: '☐',
  Constraint: '⊘',
  Stakeholder: '@',
}

// Get icon for entity type
export function getEntityIcon(type: string): string {
  return ENTITY_ICONS[type] || '•'
}

// ============================================================================
// DETECTED ENTITY HIGHLIGHT (yellow dashed underline - unconfirmed)
// ============================================================================

export interface DetectedEntityOptions {
  HTMLAttributes: Record<string, unknown>
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    detectedEntity: {
      setDetectedEntity: (attributes: {
        detectedId: string
        entityType: string
      }) => ReturnType
      unsetDetectedEntity: () => ReturnType
    }
  }
}

export const DetectedEntity = Mark.create<DetectedEntityOptions>({
  name: 'detectedEntity',

  addOptions() {
    return {
      HTMLAttributes: {},
    }
  },

  addAttributes() {
    return {
      detectedId: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-detected-id'),
        renderHTML: (attributes) => {
          if (!attributes.detectedId) return {}
          return { 'data-detected-id': attributes.detectedId }
        },
      },
      entityType: {
        default: 'Problem',
        parseHTML: (element) => element.getAttribute('data-entity-type'),
        renderHTML: (attributes) => ({
          'data-entity-type': attributes.entityType,
        }),
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-detected-id]',
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    const entityType = (HTMLAttributes['data-entity-type'] || 'problem').toLowerCase()
    return [
      'span',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        class: `detected-entity-highlight detected-entity-highlight--${entityType}`,
      }),
      0,
    ]
  },

  addCommands() {
    return {
      setDetectedEntity:
        (attributes) =>
        ({ commands }) => {
          return commands.setMark(this.name, attributes)
        },
      unsetDetectedEntity:
        () =>
        ({ commands }) => {
          return commands.unsetMark(this.name)
        },
    }
  },
})
